import { useEffect, useId, useRef, type ReactNode } from 'react';
import { addToastHost } from '../lib/toast';

/**
 * A modal on the native `<dialog>`: `showModal()` gives focus containment, an
 * inert page, Escape and focus back on the opener. Escape, a press on the
 * backdrop or `onClose` from inside all close it. Children mount only while
 * open, so each opening starts from fresh state. Below md it is a sheet along
 * the bottom of the screen. While open it hosts the toaster, so a toast
 * raised from inside it can still be dismissed.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // A press that starts inside and is released on the backdrop (selecting
  // text in a field, say) must not close it — only one that starts there.
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !open) return;
    if (!dialog.open) dialog.showModal();
    const removeToastHost = addToastHost(dialog);
    return () => {
      removeToastHost();
      if (dialog.open) dialog.close();
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
      }}
    >
      {open && (
        <div className="dialog-body">
          <h2 id={titleId} className="heading-section">
            {title}
          </h2>
          {children}
        </div>
      )}
    </dialog>
  );
}
